/**
 * `/api/v1/search/suggestions` — what the list is, and what a key does to it.
 *
 * The keyboard half is a decision table for the reason `playback/input.ts` and `ui/focus.ts` give: a
 * decision table written inside an event handler is a decision table nobody tests, and a TV needs the
 * identical one from a D-pad. It is not `focusMoveFor`, though it looks close — a suggestion list has
 * a state that a roving group does not, which is **"the input, with nothing highlighted"**. That is
 * where typing leaves you, it is what `↑` from the first row must return to, and it is the difference
 * between Enter searching for what you typed and Enter searching for what the server guessed.
 * Folding it into `-1` inside a roving group would make every caller re-derive that meaning.
 */

/** `{query, suggestions[]}` — the envelope, kept because the echoed query is what makes it usable. */
export interface SearchSuggestions {
  /**
   * The query the instance answered *for*. Not necessarily the one on screen: the request is
   * debounced and the reply is asynchronous, so this is how a caller knows whether a list still
   * describes what is in the box. Note it comes back **normalised** — `q=zzzqqqxxx+nonsense+42`
   * echoes as `zzzqqqxxx nonsense 42`.
   */
  readonly query: string;
  readonly suggestions: readonly string[];
}

/** Nothing highlighted: focus is in the text field and Enter means "search what I typed". */
export const NO_SUGGESTION = -1;

export interface SuggestionListState {
  readonly count: number;
  /** {@link NO_SUGGESTION} or an index into the list. Out-of-range is read as `NO_SUGGESTION`. */
  readonly activeIndex: number;
  /** A closed list claims no keys at all — `↓` on a page with a stale list must not reopen it. */
  readonly open: boolean;
}

export type SuggestionCommand =
  /** Highlight this row (or {@link NO_SUGGESTION} to go back to the raw text). */
  | { readonly kind: 'highlight'; readonly index: number }
  /** Take the highlighted suggestion and search for it. */
  | { readonly kind: 'accept'; readonly index: number }
  /** Search for whatever is typed, ignoring the list. */
  | { readonly kind: 'submit' }
  /** Dismiss the list and keep the text. */
  | { readonly kind: 'close' };

/**
 * `null` means **not ours** — the caller must not `preventDefault`. That matters for more keys here
 * than in a grid: Home and End belong to the *text field* while a combobox is open (they move the
 * caret, which is what someone editing a query wants), and Tab must move focus rather than accept a
 * row. Claiming either would make the box worse to type in than a plain input.
 */
export function suggestionKeyToCommand(key: string, state: SuggestionListState): SuggestionCommand | null {
  // Escape is claimed even with the list closed, because a caller may want it to clear the field;
  // this returns `close` and lets the caller decide what closing an already-closed list means.
  if (key === 'Escape') return { kind: 'close' };
  if (!state.open || state.count <= 0) {
    // Enter still submits — a search with no suggestions is an ordinary search.
    return key === 'Enter' ? { kind: 'submit' } : null;
  }

  const active =
    state.activeIndex >= 0 && state.activeIndex < state.count ? state.activeIndex : NO_SUGGESTION;

  if (key === 'Enter') {
    return active === NO_SUGGESTION ? { kind: 'submit' } : { kind: 'accept', index: active };
  }

  if (key === 'ArrowDown') {
    // Wraps at the bottom back to the raw text rather than to the first row: the thing after the last
    // suggestion is what you typed, and a list that cycles forever gives no way back to it.
    const next = active === NO_SUGGESTION ? 0 : active + 1;
    return { kind: 'highlight', index: next >= state.count ? NO_SUGGESTION : next };
  }

  if (key === 'ArrowUp') {
    const previous = active === NO_SUGGESTION ? state.count - 1 : active - 1;
    return { kind: 'highlight', index: previous < 0 ? NO_SUGGESTION : previous };
  }

  return null;
}

/**
 * Is this list still about what is in the box? The check is on the *trimmed* text because the server
 * echoes a normalised query (runs of whitespace collapsed), and a trailing space someone is about to
 * type over should not blank the list they are looking at.
 */
export function suggestionsMatch(suggestions: SearchSuggestions | null, typed: string): boolean {
  if (suggestions === null) return false;
  return suggestions.query.trim() === typed.trim().replace(/\s+/g, ' ');
}

/**
 * How long to wait after a keystroke before asking. 200 ms is a typing pause rather than a
 * round-trip: the endpoint answers in well under that, so the delay is entirely about not spending a
 * request per character — nine of them for "linux mint".
 */
export const SUGGESTION_DEBOUNCE_MS = 200;
